import { useState, useCallback, useRef, useEffect } from 'preact/hooks'
import { AlertCircle, ChevronDown, Globe, Loader2, ShieldAlert } from 'lucide-preact'
import { useKnowledgeBase } from '@/hooks/useKnowledgeBase'
import { useKnowledgeBaseSignalR, type KnowledgeBasePopulatedNotification } from '@/hooks/useKnowledgeBaseSignalR'
import { useToast } from '@/hooks/useToast'
import { useCompany } from '@/hooks/useCompany'
import { useAuth } from '@/auth/useAuth'
import { knowledgeBaseApi, type KnowledgeBaseOwner } from '@/api/knowledgeBase'
import { KnowledgeBaseCategoryRow } from './KnowledgeBaseCategoryRow'
import { KnowledgeBaseCategory, KnowledgeBaseLevel } from '@/types/knowledgeBase'
import type { AddressDto } from '@/types/company'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { routes } from '@/utils/routes'
import { parseServerTimestamp } from '@/utils/time'
import { holdsCompanyAdminRole } from '@/components/console/admin/useCompanyAdminAccess'
import { dayOf } from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { entryState, groupEntries } from '@/components/console/knowledgeBase/knowledgeBaseModel'
import { websiteLabel } from '@/utils/websiteLabel'
import { BrandGuidelinesRow } from './BrandGuidelinesRow'
import { KnowledgeBaseReadiness } from './KnowledgeBaseReadiness'
import { entryOwnerNote, levelMayHoldEntry, type LevelNames } from './knowledgeBaseLevels'
import { useBrandGuidelines } from './brandGuidelines/useBrandGuidelines'
import { CONFIRM_BUTTON, SECONDARY_BUTTON } from './brandGuidelines/brandGuidelinesStyles'

interface Props {
  clientCompanyId: string
  workspaceId?: string
  campaignId?: string
  level: KnowledgeBaseLevel
  /** Whether the current user is a company admin, on the company's own page (controls AI Populate visibility) */
  isAdmin?: boolean
}

const GROUP_HEADING = 'px-6 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400'

/**
 * The current app's knowledge base (ORB-904): one readiness band, the entries in the console's groups, and each entry's
 * state as the server reports it. Brand Guidelines shows its role colors and opens its own page.
 */
export function KnowledgeBasePanel({ clientCompanyId, workspaceId, campaignId, level, isAdmin }: Props) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [populating, setPopulating] = useState(false)
  const [showPopulateMenu, setShowPopulateMenu] = useState(false)
  const [populateOverwrite, setPopulateOverwrite] = useState(false)
  const populateMenuRef = useRef<HTMLDivElement>(null)
  const { showSuccess, showError, showInfo, showWarning } = useToast()
  const { userProfile, userCompanyRoleSets } = useAuth()
  const { company, workspaces } = useCompany()
  const companyName = (company?.Id === clientCompanyId ? company?.Name : null)
    ?? workspaces.find(workspace => workspace.ClientCompanyId === clientCompanyId)?.ClientCompanyName
    ?? null
  const workspace = workspaceId ? workspaces.find(candidate => candidate.Id === workspaceId) ?? null : null
  const names: LevelNames = { company: companyName, workspace: workspace?.Name ?? null }
  const { canEdit: workspaceCanEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)
  // Workspace/campaign KB editing requires workspace edit capability; company-level KB
  // uses the same admin flag the panel already uses for AI Populate (the server enforces
  // RequireCustomerAccess on company KB regardless).
  const userCanEdit = level === 'Company' ? !!isAdmin : workspaceCanEdit

  // Populate is a company admin's at any level (ORB-817). A workspace's run reads the workspace's own website, so a
  // workspace without one has nothing to run on (ORB-1027).
  const websiteToRead = level === KnowledgeBaseLevel.Workspace && workspace?.Website ? websiteLabel(workspace.Website) : null
  const populateOwner: KnowledgeBaseOwner | null = level === KnowledgeBaseLevel.Company
    ? (isAdmin ? { clientCompanyId } : null)
    : level === KnowledgeBaseLevel.Workspace && workspaceId && websiteToRead && holdsCompanyAdminRole(userCompanyRoleSets, clientCompanyId)
      ? { clientCompanyId, workspaceId }
      : null
  const populateLabel = websiteToRead ? `Populate from ${websiteToRead}` : 'Populate from website'
  // The run this page starts: the company's, or this workspace's. A campaign starts none.
  const ownRunWorkspaceId = level === KnowledgeBaseLevel.Company ? null : level === KnowledgeBaseLevel.Workspace ? workspaceId ?? null : undefined

  const {
    summary,
    categories,
    loading,
    error,
    activeEntry,
    entryLoading,
    fetchEntry,
    upsertEntry,
    deleteOverride,
    uploadFile,
    deleteFile,
    saving,
    refetchSummary,
  } = useKnowledgeBase({ clientCompanyId, workspaceId, campaignId, level })

  // Its row shows the colors emails and pages use, which only the entry carries. The level's own endpoint answers with
  // the company's, since Brand Guidelines is set company-wide.
  const brand = useBrandGuidelines({
    clientCompanyId,
    workspaceId: level === 'Company' ? undefined : workspaceId,
    campaignId: level === 'Campaign' ? campaignId : undefined,
  })
  const reloadBrand = brand.reload

  // Listen for async AI populate completion via SignalR
  // ⚑ Filtered on the company: the push goes to the person, who can have a run going for another customer (ORB-830).
  // ⚑ And on the workspace the run was for (ORB-1027). A company run reaches every workspace's inherited entries, so it
  // refreshes a workspace's page too; only this page's own run ends its Populating and says it finished.
  const handlePopulateCompleted = useCallback((notification: KnowledgeBasePopulatedNotification) => {
    if (notification.clientCompanyId !== clientCompanyId) return
    const runWorkspaceId = notification.workspaceId ?? null
    if (runWorkspaceId !== null && runWorkspaceId !== workspaceId) return
    if (runWorkspaceId === ownRunWorkspaceId) {
      showSuccess(
        'AI Population Complete',
        `Populated ${notification.populatedCount} categories, skipped ${notification.skippedCount}`
      )
      setPopulating(false)
    }
    refetchSummary()
    void reloadBrand()
    // Refresh the expanded entry if one is open
    if (expandedCategory) {
      fetchEntry(expandedCategory)
    }
  }, [clientCompanyId, workspaceId, ownRunWorkspaceId, showSuccess, refetchSummary, reloadBrand, expandedCategory, fetchEntry])

  // A run for this company was already going, so this request started nothing: not a failure, and the button is freed
  // either way (ORB-830).
  const handlePopulateFailed = useCallback((notification: KnowledgeBasePopulatedNotification) => {
    if (notification.clientCompanyId !== clientCompanyId || (notification.workspaceId ?? null) !== ownRunWorkspaceId) return
    if (notification.status === 'AlreadyRunning') {
      showWarning('AI Population Already Running', notification.errorMessage || 'A run for this company is already going.')
    } else {
      showError('AI Population Failed', notification.errorMessage || 'An error occurred during AI population')
    }
    setPopulating(false)
  }, [clientCompanyId, ownRunWorkspaceId, showError, showWarning])

  useKnowledgeBaseSignalR({
    userId: userProfile?.Id,
    onPopulateCompleted: handlePopulateCompleted,
    onPopulateFailed: handlePopulateFailed,
  })

  // Close the populate popover on an outside click or Escape.
  useEffect(() => {
    if (!showPopulateMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (populateMenuRef.current && !populateMenuRef.current.contains(e.target as Node)) {
        setShowPopulateMenu(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowPopulateMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [showPopulateMenu])

  const handleToggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category)
  }

  const openCategory = (category: string) => {
    if (expandedCategory === category) return
    setExpandedCategory(category)
    void fetchEntry(category)
  }

  // The company's own address, for a workspace's own one to say what it replaces. Read once its row is first opened.
  const [companyAddress, setCompanyAddress] = useState<AddressDto | null | undefined>(undefined)
  const ownAddressOpen = level === KnowledgeBaseLevel.Workspace && expandedCategory === KnowledgeBaseCategory.CompanyAddress
    && categories.some(status => status.Category === KnowledgeBaseCategory.CompanyAddress && status.HasEntryAtThisLevel)
  useEffect(() => {
    if (!ownAddressOpen || companyAddress !== undefined) return
    let current = true
    void knowledgeBaseApi.getCompanyEntry(clientCompanyId, KnowledgeBaseCategory.CompanyAddress).then(response => {
      if (current) setCompanyAddress(response.data?.Address ?? null)
    })
    return () => { current = false }
  }, [ownAddressOpen, clientCompanyId, companyAddress])

  // Fire-and-forget AI populate — sends request, shows toast, waits for SignalR
  const handleAiPopulateAll = async (overwrite: boolean) => {
    if (!populateOwner) return
    setShowPopulateMenu(false)
    setPopulating(true)
    const res = await knowledgeBaseApi.aiPopulate(populateOwner, {
      OverwriteExisting: overwrite,
    })
    if (res.error) {
      showError('Error', res.error)
      setPopulating(false)
    } else {
      showInfo('Populate started', `Webster is reading ${websiteToRead ?? 'the website'}. You will be notified when it finishes.`)
    }
  }

  const handleAiPopulateCategory = async (category: string) => {
    if (!populateOwner) return
    setPopulating(true)
    const res = await knowledgeBaseApi.aiPopulateCategory(populateOwner, category, {
      OverwriteExisting: true,
    })
    if (res.error) {
      showError('Error', res.error)
      setPopulating(false)
    } else {
      showInfo('AI Population Started', `Repopulating "${category}". You will be notified when complete.`)
    }
  }

  if (loading) return <KnowledgeBaseSkeleton />

  if (error || !summary) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 py-16">
        <p className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-white">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" aria-hidden="true" />
          The knowledge base could not load.
        </p>
        {error && <p className="text-sm text-slate-600 dark:text-slate-300">{error}</p>}
        <button type="button" className={SECONDARY_BUTTON} onClick={() => void refetchSummary()}>Try again</button>
      </div>
    )
  }

  const populatedCount = categories.filter(c => c.IsPopulated).length
  const groups = groupEntries(categories)

  // What Webster last read for this level: its own entries' drafts, not those it takes from the company.
  const lastRead = categories.filter(c => level === KnowledgeBaseLevel.Company || c.HasEntryAtThisLevel).reduce<string | null>((latest, c) => {
    const at = parseServerTimestamp(c.AiGeneratedAt)
    if (at === null) return latest
    return latest === null || at > (parseServerTimestamp(latest) ?? 0) ? c.AiGeneratedAt ?? null : latest
  }, null)

  // Brand Guidelines' own page at this level (ORB-904): the company's and a workspace's change it, and a campaign's reads
  // its workspace's (ORB-1027).
  const brandGuidelinesHref = level === 'Company'
    ? routes.settingsBrandGuidelines(clientCompanyId)
    : workspaceId
      ? (campaignId ? routes.scoped(clientCompanyId, workspaceId).campaignBrandGuidelines(campaignId) : routes.scoped(clientCompanyId, workspaceId).brandGuidelines())
      : undefined

  const address = categories.find(c => c.Category === KnowledgeBaseCategory.CompanyAddress)
  const canAddAddress = !!address && userCanEdit && levelMayHoldEntry(level, address.Config)
  const brandRoles = brand.load.kind === 'ready' ? brand.load.entry?.BrandTokens?.Roles ?? null : undefined
  const missingColorRoles = brandRoles === undefined ? null
    : (['Background', 'Text'] as const).filter(role => !brandRoles?.[role].Hex)

  // At workspace/campaign level, check if company-level KB has any content
  const showSetupBanner = level !== 'Company' && populatedCount === 0

  return (
    <div className="max-w-[960px] flex flex-col gap-7">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-8">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Knowledge Base</h1>
          <p className="mt-1 max-w-[64ch] text-sm text-slate-600 dark:text-slate-300">{lede(level, names, websiteToRead)}</p>
        </div>

        {populateOwner && (
          <div className="flex-none flex flex-col items-start sm:items-end gap-2">
            <div className="relative" ref={populateMenuRef}>
              <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={showPopulateMenu}
                onClick={() => setShowPopulateMenu(!showPopulateMenu)}
                disabled={populating}
                className={SECONDARY_BUTTON}
              >
                {populating
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : <Globe className="w-4 h-4" aria-hidden="true" />}
                {populating ? 'Populating...' : populateLabel}
                {!populating && <ChevronDown className="w-4 h-4" aria-hidden="true" />}
              </button>

              {showPopulateMenu && (
                <div role="dialog" aria-label={populateLabel}
                  className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-80 max-w-[calc(100vw-3rem)] z-10 p-4 flex flex-col gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {websiteToRead
                      ? `Webster reads ${websiteToRead} and drafts ${names.workspace ?? 'this workspace'}'s own entries.`
                      : 'Webster reads the company website and drafts every entry it can.'}
                    {' '}Words a person wrote or reviewed, and confirmed colors, stay as they are.
                  </p>
                  {/* Offered only when the run would spare something, so ticking it changes what the run does (ORB-881). */}
                  {summary.WebsiteRunWouldSpare && (
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={populateOverwrite}
                        onChange={(e) => setPopulateOverwrite((e.target as HTMLInputElement).checked)}
                        className="mt-0.5 rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        Replace those too
                        <span className="block text-xs text-slate-500 dark:text-slate-400">Anything a person set in the brand stays as it is.</span>
                      </span>
                    </label>
                  )}
                  <button type="button" onClick={() => handleAiPopulateAll(summary.WebsiteRunWouldSpare && populateOverwrite)} className={`justify-center ${CONFIRM_BUTTON}`}>
                    Populate
                  </button>
                </div>
              )}
            </div>
            {lastRead && <span className="text-xs text-slate-500 dark:text-slate-400">Webster last read {websiteToRead ?? 'the website'} on {dayOf(lastRead)}</span>}
          </div>
        )}
      </div>

      {showSetupBanner && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Knowledge Base not configured
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
              The Knowledge Base is filled in company-wide first. Ask a company admin to add it in Admin settings, or to
              populate it from the website.
            </p>
          </div>
        </div>
      )}

      <section aria-label="Knowledge base entries" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden pb-3">
        <KnowledgeBaseReadiness
          summary={summary}
          entries={categories}
          missingColorRoles={missingColorRoles}
          onAddAddress={canAddAddress ? () => openCategory(KnowledgeBaseCategory.CompanyAddress) : undefined}
          colorsHref={level !== KnowledgeBaseLevel.Campaign && userCanEdit ? brandGuidelinesHref : undefined}
        />

        {groups.map((group, index) => {
          const headingId = `kb-group-${index}`
          return (
            <section key={group.label} aria-labelledby={headingId}>
              <h2 id={headingId} className={`${GROUP_HEADING} ${index === 0 ? 'pt-6' : 'pt-8'}`}>{group.label}</h2>
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {group.entries.map((cat) => {
                  const state = entryState(cat, summary)
                  return (
                    <li key={cat.Category} className="mx-6">
                      {cat.Category === KnowledgeBaseCategory.BrandGuidelines && brandGuidelinesHref ? (
                        <BrandGuidelinesRow status={cat} state={state} href={brandGuidelinesHref} brand={brand.load}
                          ownerNote={entryOwnerNote(cat, level, names)} />
                      ) : (
                        <KnowledgeBaseCategoryRow
                          categoryStatus={cat}
                          state={state}
                          level={level}
                          isExpanded={expandedCategory === cat.Category}
                          onToggle={() => handleToggleCategory(cat.Category)}
                          entry={expandedCategory === cat.Category ? activeEntry : null}
                          entryLoading={expandedCategory === cat.Category && entryLoading}
                          onFetchEntry={fetchEntry}
                          onSave={upsertEntry}
                          onDeleteOverride={deleteOverride}
                          onUploadFile={uploadFile}
                          onDeleteFile={deleteFile}
                          saving={saving}
                          clientCompanyId={clientCompanyId}
                          userCanEdit={userCanEdit}
                          onAiPopulate={populateOwner && cat.Config.AiPopulatable && !populating ? handleAiPopulateCategory : undefined}
                          names={names}
                          companyAddress={cat.Category === KnowledgeBaseCategory.CompanyAddress ? companyAddress : undefined}
                        />
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          )
        })}
      </section>
    </div>
  )
}

function lede(level: KnowledgeBaseLevel, names: LevelNames, website: string | null): string {
  if (level === 'Company') {
    return `${names.company ? `${names.company}'s` : 'The'} company-wide entries. Webster writes every email and landing page from them, and every workspace and campaign starts with them.`
  }
  if (level === 'Workspace') {
    const workspace = names.workspace ?? 'this workspace'
    const own = website ? `${workspace} has its own website, ${website}, and where it has its own entry` : 'Where it has its own entry'
    return `What Webster writes ${workspace}'s emails and landing pages from. ${own}, Webster uses that in place of ${names.company ?? 'the company'}'s.`
  }
  return "What Webster writes this campaign's emails and landing pages from. An entry this campaign does not set for itself comes from its workspace or the company."
}

/** The page's own footprint while it loads, so nothing jumps when the entries arrive. */
function KnowledgeBaseSkeleton() {
  const block = 'bg-shimmer-light animate-shimmer rounded-md'
  return (
    <div aria-busy="true" className="max-w-[960px] flex flex-col gap-7">
      <span className="sr-only" role="status">Loading the knowledge base</span>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Knowledge Base</h1>
        <span className={`${block} h-4 w-96 max-w-full`} />
      </div>
      <div aria-hidden="true" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <div className="px-6 py-4 flex gap-7 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
          <span className={`${block} h-4 w-32`} />
          <span className={`${block} h-4 w-44`} />
          <span className={`${block} h-4 w-20`} />
        </div>
        <div className="px-6 pt-6 pb-3 flex flex-col divide-y divide-slate-200 dark:divide-slate-700">
          {[0, 1, 2, 3, 4, 5].map(index => (
            <div key={index} className="py-3.5 grid grid-cols-[minmax(0,1fr)_16px] sm:grid-cols-[minmax(0,1fr)_176px_16px] gap-x-6">
              <span className="flex flex-col gap-1.5">
                <span className={`${block} h-4 w-40`} />
                <span className={`${block} h-4 w-80 max-w-full`} />
              </span>
              <span className={`${block} h-4 w-24 hidden sm:block`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
